<template>
    <EChart v-if="chartReady" :option="chartOption"
        @chart-mouseover="onSeriesHover" @chart-mouseout="onSeriesOut" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import tooltipFocusMixin from "./tooltipFocusMixin";

// Anchors: barra (Cantidad de Clientes) = teal oscuro del sistema; línea
// (Ticket Promedio) = teal claro/brillante para contrastar con la barra
// sobre el mismo eje X. Ambos comparten familia cromática del tab.
const BAR_ANCHOR = "--report-chart-segment-1";
const LINE_ANCHOR = "--report-chart-accent";
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const TEXT_ANCHOR = "--report-text";
const BORDER_ANCHOR = "--report-border";
const TOOLTIP_BG_ANCHOR = "--report-tooltip-bg";
const TOOLTIP_TEXT_ANCHOR = "--report-tooltip-text";

export default {
    name: "SalesClientsActivityChart",

    components: { EChart },

    mixins: [tooltipFocusMixin],

    props: {
        monthHeaders: { type: Array, required: true },
        clientCounts: { type: Array, required: true },
        avgTickets: { type: Array, required: true },
        // Mismo patrón que SalesClientsStackedChart: controla `showContent` del
        // tooltip sin tocar el axisPointer ni el highlight de las series.
        showTooltip: { type: Boolean, default: true },
    },

    data() {
        return {
            chartReady: false,
            barColor: "",
            lineColor: "",
            textColor: "",
            tooltipBg: "",
            tooltipText: "",
            textMutedColor: "",
            borderColor: "",
        };
    },

    computed: {
        chartOption() {
            return {
                grid: {
                    top: 48,
                    right: 56,
                    bottom: 32,
                    left: 40,
                    containLabel: false,
                },
                legend: {
                    show: true,
                    left: 0,
                    top: 4,
                    itemGap: 16,
                    data: [
                        // Cantidad: cuadrado relleno (la barra dark del eje Y
                        // izquierdo). Ticket: línea + dot (el line dual-axis
                        // del eje Y derecho). Cada legend.data trae su propio
                        // ícono coherente con cómo aparece en el chart.
                        {
                            name: "Cantidad de Clientes",
                            icon: "rect",
                        },
                        {
                            name: "Ticket Promedio ($)",
                            icon: "path://M0 8 L24 8 M12 4 a4 4 0 1 0 0.001 0",
                        },
                    ],
                    itemWidth: 14,
                    itemHeight: 8,
                    textStyle: {
                        color: this.textColor,
                        fontSize: 12,
                        fontWeight: 500,
                    },
                },
                tooltip: {
                    trigger: "axis",
                    // `showContent: false` apaga el popover y conserva sólo el
                    // efecto de hover sobre la serie (highlight del bar/line).
                    showContent: this.showTooltip,
                    // `confine: true` evita que el tooltip se salga del card
                    // al hovear los extremos del eje X — mismo patrón que el
                    // stacked chart vecino para consistencia visual.
                    confine: true,
                    extraCssText: "max-width:260px; white-space:normal; word-break:break-word;",
                    backgroundColor: this.tooltipBg,
                    borderColor: this.tooltipBg,
                    textStyle: { color: this.tooltipText, fontSize: 12 },
                    formatter: (params) => {
                        if (!Array.isArray(params) || params.length === 0) return "";
                        // Formatear cada serie con su unidad correspondiente:
                        // count = entero, ticket = CLP. Sin esto el tooltip
                        // imprimiría números crudos y "8" sería ambiguo (8
                        // clientes vs $8 pesos).
                        const rows = params.filter((p) => p.value != null).map((p) => {
                            const value = p.seriesName === "Cantidad de Clientes"
                                ? `${p.value}`
                                : parseToMoney(p.value);
                            return `
                                <div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;${this.focusRowStyle(p.seriesName)}">
                                    <span style="min-width:0;">${p.marker}${p.seriesName}</span>
                                    <span style="flex-shrink:0;">${value}</span>
                                </div>
                            `;
                        }).join("");
                        return `
                            <div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>
                            ${rows}
                        `;
                    },
                },
                xAxis: {
                    type: "category",
                    data: this.monthHeaders,
                    axisLine: { lineStyle: { color: this.borderColor } },
                    axisTick: { show: false },
                    axisLabel: {
                        color: this.textMutedColor,
                        fontSize: 11,
                        margin: 12,
                    },
                },
                // Dos ejes Y — el izquierdo manda los splitLines (entero) para
                // que la grid coincida con números redondos. El derecho sólo
                // pinta labels (no líneas) para no duplicar la grid.
                yAxis: [
                    {
                        type: "value",
                        position: "left",
                        minInterval: 1,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: {
                            lineStyle: { color: this.borderColor, type: "dashed" },
                        },
                        axisLabel: {
                            color: this.textMutedColor,
                            fontSize: 11,
                            formatter: (val) => Math.round(val),
                        },
                    },
                    {
                        type: "value",
                        position: "right",
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { show: false },
                        axisLabel: {
                            color: this.textMutedColor,
                            fontSize: 11,
                            formatter: (val) => parseToCompactMoney(val),
                        },
                    },
                ],
                series: [
                    {
                        name: "Cantidad de Clientes",
                        type: "bar",
                        yAxisIndex: 0,
                        barMaxWidth: 36,
                        itemStyle: {
                            color: this.barColor,
                            borderRadius: 0,
                        },
                        emphasis: {
                            focus: "series",
                            itemStyle: { opacity: 0.9 },
                        },
                        data: this.clientCounts,
                    },
                    {
                        name: "Ticket Promedio ($)",
                        type: "line",
                        yAxisIndex: 1,
                        smooth: true,
                        showSymbol: false,
                        // Cuando el count es 0 el store reporta ticket 0. Esa
                        // discontinuidad significa "sin clientes activos", no
                        // "ticket cero" — rompemos la línea ahí para no engañar
                        // con una pendiente que no representa nada.
                        connectNulls: false,
                        lineStyle: {
                            width: 2,
                            color: this.lineColor,
                        },
                        itemStyle: { color: this.lineColor },
                        emphasis: { focus: "series" },
                        data: this.avgTickets.map(
                            (value, idx) => (this.clientCounts[idx] > 0 ? value : null)
                        ),
                    },
                ],
            };
        },
    },

    mounted() {
        this.resolveCssColors();
        this.chartReady = Boolean(this.barColor && this.lineColor);
    },

    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            // Mismo criterio que los otros charts: leemos los tokens del host
            // dentro del [data-theme] del tab (no de documentElement, que devuelve
            // siempre la paleta del :root e ignora el tema del tab). En mounted el
            // root puede ser un comment (v-if), así que caemos al parentElement.
            const host = this.$el && this.$el.nodeType === 1
                ? this.$el
                : (this.$el && this.$el.parentElement) || document.documentElement;
            const root = getComputedStyle(host);
            this.barColor = root.getPropertyValue(BAR_ANCHOR).trim();
            this.lineColor = root.getPropertyValue(LINE_ANCHOR).trim();
            this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
            this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
            this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
            this.tooltipBg = root.getPropertyValue(TOOLTIP_BG_ANCHOR).trim();
            this.tooltipText = root.getPropertyValue(TOOLTIP_TEXT_ANCHOR).trim();
        },
    },
};
</script>

<style scoped lang="scss">
:deep(.echart) {
    width: 100%;
    height: 100%;
}
</style>
